import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { CircleProjectionResult } from '../../../model/ProjectionResult';
import type { ResolvedCircleReferenceSource } from '../SketchReferenceSourceResolver';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	buildProjectedPlanarBasis,
	decomposeProjectedEllipseBasis,
	isProjectedCircle,
	isProjectedLine,
	localPointToWorldCoordinate,
} from './SupportProjectionPreprocessor';

/**
 * 圆源投影预处理器。
 * 说明：
 * 1. 统一把源圆视为来源平面上的单位圆经二维线性映射后的结果；
 * 2. 由同一套分解逻辑稳定产出 circle / ellipse / line 三种结果；
 * 3. 不再依赖“法向夹角”近似规则，避免圆与圆弧投影公式分叉。
 */
export class CircleSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedCircleReferenceSource, CircleProjectionResult>
{
	/**
	 * 执行圆源投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回圆/椭圆/线结果，完全退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedCircleReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): CircleProjectionResult | null {
		const projectedBasis = buildProjectedPlanarBasis({
			centerWorld: input.source.centerWorld,
			planeXAxisWorld: input.source.planeXAxisWorld,
			planeYAxisWorld: input.source.planeYAxisWorld,
			radiusX: input.source.radius,
			radiusY: input.source.radius,
			targetReferencePlane: input.targetReferencePlane,
		});
		const ellipse = decomposeProjectedEllipseBasis(projectedBasis);
		if (!ellipse) {
			return null;
		}

		if (isProjectedLine(ellipse)) {
			const startLocal = ellipse.centerLocal.clone()
				.addScaledVector(ellipse.majorAxisUnitLocal, -ellipse.radiusX);
			const endLocal = ellipse.centerLocal.clone()
				.addScaledVector(ellipse.majorAxisUnitLocal, ellipse.radiusX);
			return {
				sourceDescriptor: input.source.sourceDescriptor,
				sourceKind: 'circle',
				projectionResultKind: 'line',
				diagnostics: [],
				geometryPayload: {
					startWorld: localPointToWorldCoordinate(startLocal, input.targetReferencePlane),
					endWorld: localPointToWorldCoordinate(endLocal, input.targetReferencePlane),
				},
			};
		}

		if (isProjectedCircle(ellipse)) {
			return {
				sourceDescriptor: input.source.sourceDescriptor,
				sourceKind: 'circle',
				projectionResultKind: 'circle',
				diagnostics: [],
				geometryPayload: {
					centerWorld: localPointToWorldCoordinate(ellipse.centerLocal, input.targetReferencePlane),
					radius: (ellipse.radiusX + ellipse.radiusY) * 0.5,
				},
			};
		}

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'circle',
			projectionResultKind: 'ellipse',
			diagnostics: [],
			geometryPayload: {
				centerWorld: localPointToWorldCoordinate(ellipse.centerLocal, input.targetReferencePlane),
				radiusX: ellipse.radiusX,
				radiusY: ellipse.radiusY,
				rotation: ellipse.rotation,
			},
		};
	}
}
